import { Component, OnInit } from '@angular/core';
import { AlertController, ModalController, PopoverController } from '@ionic/angular';
import { ColumnMode, SelectionType } from '@swimlane/ngx-datatable';
import { BehaviorSubject } from 'rxjs';

import { SearchablePopoverComponent } from 'src/app/components/searchable-popover/searchable-popover.component';
import { environment } from 'src/environments/environment';
import { Profile, Tenant } from 'src/models';
import { Portal } from 'src/models/portals';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { TenantsService } from 'src/services/tenants.service';

import { CreateTenantModalComponent } from './create-tenant-modal/create-tenant-modal.component';

@Component({
  selector: 'app-super-admin-tenants',
  templateUrl: './super-admin-tenants.component.html',
  styleUrls: ['./super-admin-tenants.component.scss'],
})
export class SuperAdminTenantsComponent implements OnInit {
  public readonly tenants$: BehaviorSubject<Partial<Tenant>[]> = new BehaviorSubject([]);
  public readonly tenantUsers$: BehaviorSubject<Partial<Profile>[]> = new BehaviorSubject([]);
  public readonly tenantPortals$: BehaviorSubject<Partial<Portal>[]> = new BehaviorSubject([]);
  public isLoading = true;
  public selectedTenant: Partial<Tenant>;
  public userSearchString = '';
  public tenantSearchText = '';
  public billing: string;
  public portalImpersonateEmails: { [portalId: string]: string } = {};
  public portalSelectedUserIds: { [portalId: string]: string } = {};

  public ColumnMode = ColumnMode;
  public SelectionType = SelectionType;
  public selected = [];

  constructor(
    private tenantsService: TenantsService,
    private authService: AuthService,
    private msgService: MessageService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private popoverCtrl: PopoverController,
  ) {}

  async ngOnInit() {
    await this.loadTenants();

    this.isLoading = false;
  }

  async loadTenants() {
    const res = await this.tenantsService.list({
      limit: null,
    });

    this.tenants$.next(res);
  }

  async loadUsersForTenant() {
    const res = await this.tenantsService.listUsers(this.selectedTenant?.id || null, {
      limit: null,
      order: ['name'],
      descending: false,
      where: this.userSearchString
        ? [
            {
              field: 'name',
              operator: 'like',
              value: this.userSearchString,
            },
          ]
        : undefined,
    });

    this.tenantUsers$.next(res.data);
  }

  async onSelect(event) {
    this.selectedTenant = await this.tenantsService.getTenantById(event.selected[0].id);
    this.userSearchString = '';
    this.billing = JSON.stringify(this.selectedTenant.billing);
    this.portalImpersonateEmails = {};
    this.portalSelectedUserIds = {};

    await Promise.all([this.loadUsersForTenant(), this.loadPortalsForTenant()]);
  }

  async loadPortalsForTenant() {
    const res = await this.tenantsService.listPortals(this.selectedTenant?.id || null, {
      limit: null,
      order: ['isExternal'],
      descending: false,
    });

    this.tenantPortals$.next(res.data);
  }

  async signInAsUser(userId) {
    const customToken = await this.tenantsService.generateCustomToken(this.selectedTenant.id, userId);
    const origin = `https://${this.selectedTenant.subdomain}.${environment.rootDomain}`;

    // NOTE: Passing token using postMessage to avoid exposing it in the URL.
    window.open(`${origin}/login/impersonate`, '_blank');

    this.authService.sendCustomToken(customToken, origin);
  }

  async signInToPortal(portal: Partial<Portal>, email?: string, userId?: string, event?: Event) {
    // Stop event propagation to prevent triggering other elements
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    let userEmail: string;
    try {
      if (email) {
        userEmail = email;
      } else if (userId) {
        // Internal portal with selected user
        const user = this.tenantUsers$.value.find((u) => u.id === userId);
        if (!user?.email) {
          this.msgService.showError('Selected user does not have an email address');
          return;
        }
        userEmail = user.email;
      } else {
        this.msgService.showError('Email or user selection is required');
        return;
      }

      // Generate Firebase custom token for impersonation
      // All portals require Firebase Identity Platform to be configured
      const authToken = await this.tenantsService.generatePortalImpersonateToken(
        this.selectedTenant.id,
        portal.id!,
        userEmail,
      );

      // Use the full URL from backend (single source of truth)
      const portalUrl = (portal as any).url;
      if (!portalUrl) {
        this.msgService.showError('Portal URL not configured');
        console.error('Portal configuration error:', { portal });
        return;
      }

      // Use postMessage pattern (same as dashboard impersonation)
      // Open the portal impersonation page
      window.open(`${portalUrl}/login/impersonate`, '_blank');

      // Send the custom token via postMessage when the portal requests it
      this.authService.sendCustomToken(authToken, portalUrl);
    } catch (error: any) {
      let errorMessage = 'Failed to generate portal impersonation token';
      const userNotFoundError = error.error.errors?.[0]?.title === 'User not found';

      if (userNotFoundError) {
        errorMessage = `There is no user with the email address "${userEmail}" associated with this portal`;
      }
      this.msgService.showError(errorMessage);
    }
  }

  async createTenant() {
    const modal = await this.modalCtrl.create({
      component: CreateTenantModalComponent,
      componentProps: {},
    });

    await modal.present();

    modal.onDidDismiss().then(() => {
      this.loadTenants();
    });
  }

  // This is needed for the case when a user is initially created for a new client using username/password for
  // demo purposes, then when they convert they want to set up SSO. Once SSO is set up, that initial user will
  // need to be reset in firebase or they may not be able to login depending on their SSO settings, due to some
  // strange behavior in firebase that we could not find another fix for.
  async resetFirebaseSettings(userId: string) {
    const confirm = await this.alertCtrl.create({
      header: 'Reset the Firebase settings for this user?',
      message:
        "This will force unlink the user's Firebase settings. When they log in again they will be recreated in Firebase and relinked with a new Firebase ID.",
      buttons: [
        {
          text: 'No',
          handler: () => {},
        },
        {
          text: 'Yes',
          handler: async () => {
            await this.tenantsService.resetFirebaseSettingsForUser(this.selectedTenant.id, userId);
          },
        },
      ],
    });

    await confirm.present();

    confirm.onDidDismiss().then(() => {
      setTimeout(() => {
        this.loadUsersForTenant();
      }, 500);
    });
  }

  async verifyEmailAddress(userId: string) {
    const confirm = await this.alertCtrl.create({
      header: `Manually verify this users's email address?`,
      message:
        "This will manually verify the user's email address and allow them to log in via email and password. Only do this if you are sure the user is who they say they are.",
      buttons: [
        {
          text: 'No',
          handler: () => {},
        },
        {
          text: 'Yes',
          handler: async () => {
            await this.tenantsService.verifyEmailAddressForUser(this.selectedTenant.id, userId);
          },
        },
      ],
    });

    await confirm.present();

    confirm.onDidDismiss().then(() => {
      setTimeout(() => {
        this.loadUsersForTenant();
      }, 500);
    });
  }

  async updateTenant() {
    this.isLoading = true;

    try {
      await this.tenantsService.updateTenantById(this.selectedTenant.id, {
        accountStatus: this.selectedTenant.accountStatus,
        trialEndDate: this.selectedTenant.trialEndDate,
        autoSplittingEnabled: this.selectedTenant.autoSplittingEnabled,
        moveToNeedsResponse: this.selectedTenant.moveToNeedsResponse,
        doNotReopenConversationsOnReply: this.selectedTenant.doNotReopenConversationsOnReply,
        disableConversationsStartedByInternalUser: this.selectedTenant.disableConversationsStartedByInternalUser,
        isInternalSupport: this.selectedTenant.isInternalSupport,
        billing: JSON.parse(this.billing),
      });
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      console.log('Error updating tenant: ', e);
    }
  }

  async enableAutomations() {
    try {
      this.isLoading = true;
      await this.tenantsService.enableAutomations(this.selectedTenant.id);
      this.msgService.show('Enabled');
      await this.loadTenants();
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async enableAccounts() {
    try {
      this.isLoading = true;
      await this.tenantsService.enableAccounts(this.selectedTenant.id);

      this.msgService.show('Enabled');
      await this.loadTenants();
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async runAccountSync() {
    try {
      this.isLoading = true;
      await this.tenantsService.runAccountSync(this.selectedTenant.id);
      this.msgService.show('Account sync background tasks queued.');
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async toggleEscalations(enabled: boolean) {
    try {
      this.isLoading = true;
      await this.tenantsService.updateTenantFeatureFlags(this.selectedTenant.id, {
        escalationsEnabled: enabled,
      });
      this.selectedTenant.featureFlags = {
        ...(this.selectedTenant.featureFlags || {}),
        escalationsEnabled: enabled,
      };

      // If updating the current tenant's feature flags, refresh auth service data
      if (this.selectedTenant.id === this.authService.tenant?.id) {
        await this.authService.loadCompany();
      }

      this.msgService.show(`Escalations ${enabled ? 'enabled' : 'disabled'}`);
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async toggleAutomationAssistant(enabled: boolean) {
    try {
      this.isLoading = true;
      await this.tenantsService.updateTenantFeatureFlags(this.selectedTenant.id, {
        AUTOMATION_ASSISTANT: enabled,
      });
      this.selectedTenant.featureFlags = {
        ...(this.selectedTenant.featureFlags || {}),
        AUTOMATION_ASSISTANT: enabled,
      };

      // If updating the current tenant's feature flags, refresh auth service data
      if (this.selectedTenant.id === this.authService.tenant?.id) {
        await this.authService.loadCompany();
      }

      this.msgService.show(`Automation Assistant ${enabled ? 'enabled' : 'disabled'}`);
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async toggleKnowledgeGapAnalysisUI(enabled: boolean) {
    try {
      this.isLoading = true;
      await this.tenantsService.updateTenantFeatureFlags(this.selectedTenant.id, {
        knowledgeGapAnalysis: enabled,
      });
      this.selectedTenant.featureFlags = {
        ...(this.selectedTenant.featureFlags || {}),
        knowledgeGapAnalysis: enabled,
      };

      // If updating the current tenant's feature flags, refresh auth service data
      if (this.selectedTenant.id === this.authService.tenant?.id) {
        await this.authService.loadCompany();
      }

      this.msgService.show(`Knowledge Gap Analysis UI ${enabled ? 'enabled' : 'disabled'}`);
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async toggleKnowledgeGapAnalysisBackend(enabled: boolean) {
    try {
      this.isLoading = true;
      await this.tenantsService.updateTenantFeatureFlags(this.selectedTenant.id, {
        knowledgeGapAnalysisBackend: enabled,
      });
      this.selectedTenant.featureFlags = {
        ...(this.selectedTenant.featureFlags || {}),
        knowledgeGapAnalysisBackend: enabled,
      };

      // Note: Backend flag doesn't affect current session, no need to refresh auth

      this.msgService.show(
        `Knowledge Gap Analysis Backend ${enabled ? 'enabled' : 'disabled'} - ${enabled ? 'weekly tasks will run' : 'tasks stopped'}`,
      );
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async toggleAnalyticsAssistant(enabled: boolean) {
    try {
      this.isLoading = true;
      await this.tenantsService.updateTenantFeatureFlags(this.selectedTenant.id, {
        ANALYTICS_ASSISTANT: enabled,
      });
      this.selectedTenant.featureFlags = {
        ...(this.selectedTenant.featureFlags || {}),
        ANALYTICS_ASSISTANT: enabled,
      };

      // If updating the current tenant's feature flags, refresh auth service data
      if (this.selectedTenant.id === this.authService.tenant?.id) {
        await this.authService.loadCompany();
      }

      this.msgService.show(`Analytics Assistant ${enabled ? 'enabled' : 'disabled'}`);
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async toggleViewAgentThoughts(enabled: boolean) {
    try {
      this.isLoading = true;
      await this.tenantsService.updateTenantById(this.selectedTenant.id, {
        viewAgentThoughtsEnabled: enabled,
      });
      this.selectedTenant.viewAgentThoughtsEnabled = enabled;

      // If updating the current tenant, refresh auth service data
      if (this.selectedTenant.id === this.authService.tenant?.id) {
        await this.authService.loadCompany();
      }

      this.msgService.show(`View Agent Thoughts ${enabled ? 'enabled' : 'disabled'}`);
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async toggleCustomerPortal(enabled: boolean) {
    try {
      this.isLoading = true;
      await this.tenantsService.updateTenantFeatureFlags(this.selectedTenant.id, {
        CUSTOMER_PORTAL_ENABLED: enabled,
      });
      this.selectedTenant.featureFlags = {
        ...(this.selectedTenant.featureFlags || {}),
        CUSTOMER_PORTAL_ENABLED: enabled,
      };

      this.msgService.show(`Customer Portal ${enabled ? 'enabled' : 'disabled'}`);
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async toggleSystemMonitoring(enabled: boolean) {
    try {
      this.isLoading = true;
      await this.tenantsService.updateTenantFeatureFlags(this.selectedTenant.id, {
        systemMonitoringEnabled: enabled,
      });
      this.selectedTenant.featureFlags = {
        ...(this.selectedTenant.featureFlags || {}),
        systemMonitoringEnabled: enabled,
      };

      this.msgService.show(`System Monitoring ${enabled ? 'enabled' : 'disabled'}`);
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async toggleJsmIntegration(enabled: boolean) {
    try {
      this.isLoading = true;
      await this.tenantsService.updateTenantFeatureFlags(this.selectedTenant.id, {
        jsmIntegrationEnabled: enabled,
      });
      this.selectedTenant.featureFlags = {
        ...(this.selectedTenant.featureFlags || {}),
        jsmIntegrationEnabled: enabled,
      };

      this.msgService.show(`JSM Integration ${enabled ? 'enabled' : 'disabled'}`);
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async toggleAiUsageDashboard(enabled: boolean) {
    try {
      this.isLoading = true;
      await this.tenantsService.updateTenantFeatureFlags(this.selectedTenant.id, {
        AI_USAGE_DASHBOARD: enabled,
      });
      this.selectedTenant.featureFlags = {
        ...(this.selectedTenant.featureFlags || {}),
        AI_USAGE_DASHBOARD: enabled,
      };

      // If updating the current tenant's feature flags, refresh auth service data
      if (this.selectedTenant.id === this.authService.tenant?.id) {
        await this.authService.loadCompany();
      }

      this.msgService.show(`AI Usage Dashboard ${enabled ? 'enabled' : 'disabled'}`);
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async openUserSelectorForPortal(event: Event, portalId: string) {
    const popover = await this.popoverCtrl.create({
      component: SearchablePopoverComponent,
      componentProps: {
        loadData: async (searchText: string) => {
          const users = this.tenantUsers$.value;
          const normalizedSearch = searchText?.toLowerCase().trim() || '';

          return users
            .filter((user) => {
              if (!user.email) return false; // Only show users with email addresses

              if (!normalizedSearch) return true; // Show all users if no search text

              // Search both name and email fields
              const nameMatch = user.name?.toLowerCase().includes(normalizedSearch);
              const emailMatch = user.email?.toLowerCase().includes(normalizedSearch);

              return nameMatch || emailMatch;
            })
            .map((user) => ({
              id: user.id,
              title: user.name || user.email || 'Unknown User',
              image: user.photo,
              object: user,
            }));
        },
        callback: (userId: string, userName: string, user: any) => {
          this.portalSelectedUserIds[portalId] = userId;
        },
        showImages: true,
        showSearch: true,
      },
      event: event,
    });

    await popover.present();
  }

  getSelectedUser(portalId: string): Partial<Profile> | null {
    const userId = this.portalSelectedUserIds[portalId];
    if (!userId) return null;
    return this.tenantUsers$.value.find((user) => user.id === userId) || null;
  }
}
